/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {ReactNode, useContext, useEffect} from "react";
import * as emotion from "emotion";
import {ICONS} from "@res/icons";
import {COLORS} from "@res/colors";
import {DialogModule} from "@intuitionrobotics/thunderstorm/frontend";
import {FONTS} from "@res/fonts";
import {sortArray} from "@intuitionrobotics/ts-common";
import {padding_rl_med} from "@styles/styles";
import {HGap} from "../../widget/HGap";
import {ChatBoxInput} from "./ChatBoxInput";
import {MessagingModule} from "@modules/MessagingModule";
import {CCInbox, MessageWithStatus} from "@app/ir-q-app-common/types/message-api";
import {MessageStatuses} from "@app/ir-q-app-common/types/message";
import {Loader} from "../../../../widgets/Loader";
import {PlayerModule} from "@modules/PlayerModule";
import {DB_Patient} from "@app/app-shared/patient";
import {ChatsContext} from "./ChatsContext";
import {MessageEntry} from "./MessageEntry";
import {MessageSeparator} from "./MessageSeparator";
import {ContactsContext} from "./ContactsContext";
import {PatientsContext} from "../patients/PatientsContext";

export type ChatBoxMessage = {
    message: MessageWithStatus;
    patientContactId: string;
    answered: boolean;
}

type Props = {
    chatId: string
    onClose: () => void
    recipient: DB_Patient
}

type PropsImpl = Props & {
    messages: MessageWithStatus[],
    chat: CCInbox,
    groupContactId?: string,
    isSendingToChat: boolean
}

const headerStyle = emotion.css(
    {
        backgroundColor: COLORS.primary_dark_blue,
        minHeight: '66px',
        borderRadius: '20px 20px 0px 0px'
    }
);

const treeWrapper = emotion.css(
    {
        paddingTop: 200,
        height: '85%',
        overflowY: 'scroll',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column-reverse',
        paddingLeft: 24,
        paddingRight: 19, // 19px + 5px scroll bar
        "> div": {
            ':focus': {
                outline: "none"
            }
        }
    }
);

const chatWrapperStyle = emotion.css(
    {
        zIndex: 9991,
        marginRight: 20,
        position: 'absolute',
        height: '90%',
        width: '520px',
        bottom: 0,
        right: 0,
        pointerEvents: 'none',
        '> *': {
            pointerEvents: 'auto'
        }
    }
);

const className = emotion.css({
    maxWidth: 520,
    minWidth: 150,
    width: '100%',
    maxHeight: 'max(400px, calc(100% - 16px))', // calc for vertical line sync
    transition: 'max-height 0.2s linear',
    position: 'absolute',
    bottom: 0,
    right: 0,
    backgroundColor: "#ffffff",
    boxShadow: "1px 1px 6px 0 rgba(0, 0, 0, 0.3)",
    borderRadius: '20px 20px 2px 2px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: ' space-between'
});

export const ChatBox = (props: Props) => {
    const {saveMessages, messages, chats, isSendingToChat} = useContext(ChatsContext);
    const {contacts} = useContext(ContactsContext);
    const {demo} = useContext(PatientsContext)
    const {chatId, recipient} = props;
    const chat = chats![chatId];
    const groupContact = chat.contactIds.map(cId => contacts[cId]).find(contact => contact?.contactType === "group");
    useEffect(() => {
        if (!messages?.[chatId]) {
            if (demo)
                return;

            MessagingModule.queryMessages(chatId, groupContact?._id!)
                .then(({messages: _messages}) => saveMessages(_messages)).catch(console.error);
        }

        return () => PlayerModule.stopAllPlayers()
    }, [chatId, recipient.contactId])

    return <div className={chatWrapperStyle}>
        <div className={className}>
            {!messages?.[chatId] ? <Loader overlay={true}/> :
                <>
                    <ChatBoxImpl {...props} messages={messages[chatId]} chat={chat} groupContactId={groupContact?._id} isSendingToChat={!!isSendingToChat[chat._id]}/>
                    <ChatBoxInput patient={recipient} groupContactId={groupContact?._id} chat={chat} isSendingToChat={isSendingToChat[chat._id]}/>
                </>}
        </div>
    </div>
}

const ChatBoxImpl = (props: PropsImpl) => {
    const {demo} = useContext(PatientsContext)
    const {saveMessages, messages} = useContext(ChatsContext);
    const {chatId} = props;

    const renderConversation = (lastMessages: MessageWithStatus[]): ReactNode[] => {
        // // Get replies only.
        // const replies = lastMessages.filter(msg => {
        // 	return msg.senderId === props.patientId;
        // });
        // Get replies only.
        const replies = lastMessages.filter(msg => msg.replyId);

        // // Get only outgoing messages.
        // const originals = lastMessages.filter(msg => {
        // 	return msg.senderId !== props.patientId;
        // });

        // Get only outgoing messages.
        const originals = lastMessages.filter(msg => !replies.map(m => m._id).includes(msg._id));

        const finalArray: ReactNode[] = [];
        // Place every patient reply, right after the corresponding outgoing message.
        originals.forEach((msg, index) => {
            if (msg.status.status && msg.status.status.toUpperCase() === MessageStatuses.RETRIED.toUpperCase())
                return;

            const isLast = index === originals.length - 1;
            const replyIdx = replies.findIndex(_msg => _msg.replyId === msg._id);
            if (replyIdx !== -1) {
                // We push the reply after the corresponding outgoing,
                // and we also remove it from the replies array.
                finalArray.push(<MessageEntry key={replies[replyIdx]._id} message={replies[replyIdx]} patientContactId={props.recipient.contactId} answered={false}/>);
                finalArray.push(<MessageEntry key={msg._id} message={msg} patientContactId={props.recipient.contactId} answered={true}/>);

                replies.splice(replyIdx, 1);
            } else {
                finalArray.push(<MessageEntry key={msg._id} message={msg} patientContactId={props.recipient.contactId} answered={false}/>);
            }
            // Only add a separator if we haven't reached the end.
            if (!isLast)
                finalArray.push(<MessageSeparator key={msg._id + "_separator"}/>);
        });

        return finalArray;
    };

    const handleScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
        if (demo)
            return;

        const target = e.target as HTMLDivElement;
        const top = target.scrollHeight - 400 <= -target.scrollTop;
        if (!top)
            return;

        const openMessages = messages![chatId];
        if (openMessages.length >= 90) {
            const timestamp = openMessages[0].timestamp;
            const contactId = props.groupContactId;

            if (contactId)
                MessagingModule.queryMessages(chatId, contactId, 100, timestamp).then(({messages: _messages}) => saveMessages(_messages)).catch(console.error);
        }
    }

    const data = sortArray(props.messages, _data => _data.timestamp);

    const patient = props.recipient;
    return <>
        <div className={`ll_h_c ${headerStyle} ${padding_rl_med}`} style={{justifyContent: 'space-between'}}>
            {FONTS.medium_18_white(patient?.name || "")}
            <div className={'ll_h_c'}>
                <HGap h={20}/>
                <div className={props.isSendingToChat ? 'cursor_disabled' : 'clickable'}
                     onClick={
                         () => {
                             if (props.isSendingToChat)
                                 return;
                             props.onClose();
                             DialogModule.close()
                         }
                     }>
                    {ICONS.close(COLORS.white, 20)}
                </div>
            </div>
        </div>
        <div className={`${treeWrapper}`}
             onScroll={handleScroll}>
            {renderConversation(data)}
        </div>
    </>

}
